import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronUp, ChevronDown, Play, Pause, X, ExternalLink, Sparkles } from 'lucide-react';
import './ImageScroller.css';

const items = [
  { 
    id: 1, 
    title: 'Annual Letters to Rome (Litterae Annuae)', 
    url: 'assets/img/ImageScroll/image1.jpg',
    tag: 'Manuscripts',
    period: '1542 – 1900s',
    description: 'Centuries-old handwritten annual dispatches sent by Jesuit missionaries from the Madura Mission to the General in Rome, chronicling regional governance, local culture, and mission growth.'
  },
  { 
    id: 2, 
    title: 'Books in Tamil, English, Latin & French', 
    url: 'assets/img/ImageScroll/image2.jpg',
    tag: 'Rare Books',
    period: '16th – 19th Century',
    description: 'A rich multilingual collection of rare theological, linguistic, and historical volumes printed across Europe and early Indian presses.'
  },
  { 
    id: 3, 
    title: 'Historic Photos & Glass Plates from 1860', 
    url: 'assets/img/ImageScroll/image3.jpg',
    tag: 'Photography',
    period: '1860 – Present',
    description: 'Over thousands of rare photographic glass plates, sepia prints, and archival albums documenting early missionary expeditions, architecture, and South Indian life.'
  },
  { 
    id: 4, 
    title: 'Writings & Diaries of Fr. de Rossi (1750+)', 
    url: 'assets/img/ImageScroll/image4.jpg',
    tag: 'Historical Texts',
    period: '18th Century',
    description: 'Personal diaries, pastoral reflections, and firsthand observations of South Indian socio-political transitions during the 18th century.'
  },
  { 
    id: 5, 
    title: 'Dictionaries in Multiple Classical Languages', 
    url: 'assets/img/ImageScroll/image5.jpg',
    tag: 'Linguistics',
    period: 'Polyglot Lexicons',
    description: 'Monumental bilingual and multilingual dictionaries combining Tamil, Latin, Portuguese, and French, compiled to bridge Eastern and Western thought.'
  },
  { 
    id: 6, 
    title: 'Thiru Irudaya Thoothan (1888–2021)', 
    url: 'assets/img/ImageScroll/image6.jpg',
    tag: 'Periodicals',
    period: '130+ Years Series',
    description: 'One of the longest-running Tamil Catholic monthly periodicals, containing immense sociological, cultural, and spiritual writings over more than a century.'
  },
  { 
    id: 7, 
    title: 'Writings of Veeramamunivar (Fr. Beschi)', 
    url: 'assets/img/ImageScroll/image7.jpg',
    tag: 'Tamil Classics',
    period: '1680 – 1747',
    description: 'Works of the celebrated Italian Jesuit scholar Constantine Joseph Beschi, including the Tamil epic Thembavani, grammar treaties (Grammatica Damulica), and script reforms.'
  },
  { 
    id: 8, 
    title: 'Rare Scripts & Treatises on Palm Leaves', 
    url: 'assets/img/ImageScroll/image8.jpg',
    tag: 'Olai Chuvadi',
    period: 'Traditional Era',
    description: 'Ancient palm-leaf manuscripts (Olai Chuvadi) preserving classical literature, indigenous medical lore, astrology, and early catechisms inscribed with iron styluses.'
  },
  { 
    id: 9, 
    title: 'Caritas Archival Chronicles (1917–2017)', 
    url: 'assets/img/ImageScroll/image9.jpg',
    tag: 'Centennial Annals',
    period: '1917 – 2017',
    description: 'A comprehensive hundred-year record of humanitarian, educational, and social outreach missions across Madurai, Trichy, and Kodaikanal.'
  },
];

const ImageScroller = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const itemsToShow = 4; // Show 4 items cleanly in the sidebar
  const timerRef = useRef(null);

  const maxIndex = Math.max(0, items.length - itemsToShow);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  // Auto-scroll loop
  useEffect(() => {
    if (isPlaying && !isHovered) {
      timerRef.current = setInterval(() => {
        handleNext();
      }, 3600);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPlaying, isHovered, maxIndex]);

  const visibleItems = items.slice(currentIndex, currentIndex + itemsToShow);

  // If near end and slice is smaller than itemsToShow, loop nicely
  const displayItems = visibleItems.length < itemsToShow 
    ? [...visibleItems, ...items.slice(0, itemsToShow - visibleItems.length)]
    : visibleItems;

  return (
    <div 
      className="arch-scroller-card"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Header bar with controls */}
      <div className="arch-scroller-header">
        <div className="arch-scroller-badge">
          <Sparkles size={12} />
          <span>Curated Treasures</span>
        </div>
        
        <div className="arch-scroller-actions">
          <button 
            onClick={() => setIsPlaying(!isPlaying)} 
            className="arch-ctrl-btn arch-play-btn" 
            title={isPlaying ? "Pause auto-scroll" : "Play auto-scroll"}
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? <Pause size={14} /> : <Play size={14} />}
          </button>
          <button 
            onClick={handlePrev} 
            className="arch-ctrl-btn" 
            title="Previous collection"
            aria-label="Previous"
          >
            <ChevronUp size={16} />
          </button>
          <button 
            onClick={handleNext} 
            className="arch-ctrl-btn" 
            title="Next collection"
            aria-label="Next"
          >
            <ChevronDown size={16} />
          </button>
        </div>
      </div>

      {/* Animated Items Viewport */}
      <div className="arch-scroller-viewport">
        <AnimatePresence mode="popLayout" initial={false}>
          {displayItems.map((item, index) => (
            <motion.div
              key={`${item.id}-${currentIndex}`}
              className="arch-scroller-item"
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -25 }}
              transition={{ duration: 0.35, ease: 'easeOut', delay: index * 0.04 }}
              onClick={() => setSelectedItem(item)}
              title="Click to view details"
            >
              <div className="arch-item-thumb-box">
                <img 
                  src={item.url} 
                  alt={item.title} 
                  className="arch-item-img" 
                  loading="lazy"
                />
                <div className="arch-thumb-overlay">
                  <ExternalLink size={16} />
                </div>
              </div>

              <div className="arch-item-info">
                <span className="arch-item-tag">{item.tag}</span>
                <h5 className="arch-item-title">{item.title}</h5>
                <span className="arch-item-period">
                  <i className="bi bi-clock-history"></i> {item.period}
                </span>
              </div>

              <div className="arch-item-arrow">
                <i className="bi bi-chevron-right"></i>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Footer Status & Progress Dots */}
      <div className="arch-scroller-footer">
        <span>{currentIndex + 1}–{Math.min(currentIndex + itemsToShow, items.length)} of {items.length} collections</span>
        <div className="arch-dots-bar">
          {items.map((_, idx) => (
            <div 
              key={idx} 
              className={`arch-dot ${idx === currentIndex ? 'active' : ''}`} 
              onClick={() => setCurrentIndex(Math.min(idx, maxIndex))}
              style={{ cursor: 'pointer' }}
            />
          ))}
        </div>
      </div>

      {/* Lightbox / Modal for Detail Exploration */}
      {selectedItem && (
        <div 
          className="arch-modal-backdrop"
          onClick={() => setSelectedItem(null)}
        >
          <div 
            className="arch-modal-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="arch-modal-close-btn"
              onClick={() => setSelectedItem(null)}
              aria-label="Close"
            >
              <X size={20} />
            </button>

            <div className="arch-modal-img-wrap">
              <img 
                src={selectedItem.url} 
                alt={selectedItem.title} 
                className="arch-modal-img" 
              />
            </div>

            <div className="arch-modal-content">
              <div className="arch-modal-header-meta">
                <span className="arch-item-tag">{selectedItem.tag}</span>
                <span className="arch-item-period">
                  <i className="bi bi-clock-history me-1"></i> {selectedItem.period}
                </span>
              </div>

              <h3 className="arch-modal-title">{selectedItem.title}</h3>
              <p className="arch-modal-desc">{selectedItem.description}</p>

              <div className="arch-modal-footer">
                <span className="text-muted small">
                  <i className="bi bi-geo-alt me-1"></i> Sacred Heart College, Shembaganur
                </span>
                <button 
                  className="arch-modal-btn"
                  onClick={() => setSelectedItem(null)}
                >
                  Close Preview
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageScroller;